import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import {
    FieldInterventionEntitiesService,
    FieldInterventionEntity,
    FieldInterventionForm,
    FieldInterventionFormControllerGet200Response,
    FieldInterventionFormsService,
    PeopleService,
    Person,
    PersonControllerGet200Response,
} from '@connect-services/client/sdk/api';
import { FilterService } from '@connect-services/client/services/filter.service';
import { defaultPaginationParameters, OrderEnum } from '@shared/client/models/pagination.model';

export const DEFAULT_TAKE = 5;

export type FilterEntitiesDto = {
    entityIds?: string[];
    forms?: string[];
    layers?: string[];
    people?: string[];
    statuses?: FieldInterventionEntity.StatusEnum[];
};

@Injectable({
    providedIn: 'root',
})
export class FieldInterventionEntitiesFilterService extends FilterService<FilterEntitiesDto> {
    public readonly baseFilterParams: WritableSignal<FilterEntitiesDto | undefined> = signal(undefined);
    public readonly formsWithPagination: WritableSignal<FieldInterventionFormControllerGet200Response | null> =
        signal(null);
    public readonly operatorsWithPagination: WritableSignal<PersonControllerGet200Response | null> = signal(null);
    public readonly statuses: WritableSignal<FieldInterventionEntity.StatusEnum[]> = signal([]);

    private readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    private readonly fieldInterventionEntitiesServiceSdk = inject(FieldInterventionEntitiesService);
    private readonly formService = inject(FieldInterventionFormsService);
    private paginationParameters = { ...defaultPaginationParameters };
    private readonly peopleServiceSdk = inject(PeopleService);

    public constructor() {
        super();
    }

    public closeFilterMenu(): void {
        this.filterByParams.update(() => ({ ...this.baseFilterParams() }));
    }

    public override async initData(): Promise<void> {
        await this.loadFormsWithPagination();
        await this.loadOperatorsWithPagination();

        this.statuses.set(await this.loadStatuses());
    }

    public async loadFormsWithPagination(forcePage?: number): Promise<void> {
        const pagination = this.formsWithPagination();
        if (forcePage === undefined && pagination && !pagination!.hasNextPage) {
            return;
        }

        let page: number = forcePage !== undefined ? forcePage : (pagination?.page ?? 0);
        if (!page) {
            const loadedForms = await this.loadForms(page);

            this.formsWithPagination.set(loadedForms);

            return;
        }

        const forms: FieldInterventionForm[] = [...(pagination?.items ?? [])];
        let hasNextPage = false;
        let formsResponse: FieldInterventionFormControllerGet200Response;

        do {
            formsResponse = await this.loadForms(page);
            forms.push(...(formsResponse?.items ?? []));
            hasNextPage = formsResponse.hasNextPage;
            page += 1;
        } while (hasNextPage);

        formsResponse.items = forms;
        this.formsWithPagination.set(formsResponse);
    }

    public async loadOperatorsWithPagination(forcePage?: number): Promise<void> {
        const pagination = this.operatorsWithPagination();
        if (forcePage === undefined && pagination && !pagination!.hasNextPage) {
            return;
        }

        let page: number = forcePage !== undefined ? forcePage : (pagination?.page ?? 0);
        if (!page) {
            const loadedPeople = await this.loadPeople(page);

            this.operatorsWithPagination.set(loadedPeople);

            return;
        }

        const people: Person[] = [...(pagination?.items ?? [])];
        let hasNextPage = false;
        let peopleResponse: PersonControllerGet200Response;

        do {
            peopleResponse = await this.loadPeople(page);
            people.push(...(peopleResponse?.items ?? []));
            hasNextPage = peopleResponse.hasNextPage;
            page += 1;
        } while (hasNextPage);

        peopleResponse.items = people;
        this.operatorsWithPagination.set(peopleResponse);
    }

    public resetCheckboxState(): void {
        this.filterByParams.set(undefined);
    }

    public resetData(): void {
        this.formsWithPagination.set(null);
        this.filterByParams.set({});
        this.operatorsWithPagination.set(null);
        this.statuses.set([]);
        this.resetCheckboxState();
    }

    public submitFilter(): void {
        this.baseFilterParams.set(this.filterByParams());
    }

    public toggleSelection(value: string, key: keyof FilterEntitiesDto): void {
        if (key === 'statuses') {
            return;
        }

        if (!this.filterByParams() || !(key in this.filterByParams()!)) {
            this.filterByParams.update((dto: FilterEntitiesDto | undefined) => {
                return { ...dto, [key]: [] };
            });
        }

        if (!this.filterByParams()![key]!.includes(value)) {
            this.filterByParams.update((dto: FilterEntitiesDto | undefined) => {
                return { ...dto, [key]: [...dto![key]!, value] };
            });

            return;
        }
        this.filterByParams.update((dto: FilterEntitiesDto | undefined) => {
            if (!dto?.[key]) {
                return dto;
            }

            return { ...dto, [key]: dto[key]!.filter((id: string) => id !== value) };
        });
    }

    public toggleStatusSelection(
        value: FieldInterventionEntity.StatusEnum,
        options: { forceToggle?: boolean } = {},
    ): void {
        if (!this.statuses().includes(value) && !options.forceToggle) {
            return;
        }
        if (!this.filterByParams() || !('statuses' in this.filterByParams()!)) {
            this.filterByParams.update((dto: FilterEntitiesDto | undefined) => {
                return { ...dto, statuses: [] };
            });
        }

        if (!this.filterByParams()!.statuses!.includes(value)) {
            this.filterByParams.update((dto: FilterEntitiesDto | undefined) => {
                return { ...dto, statuses: [...dto!.statuses!, value] };
            });

            return;
        }
        this.filterByParams.update((dto: FilterEntitiesDto | undefined) => {
            if (!dto?.statuses) {
                return dto;
            }

            return {
                ...dto,
                statuses: dto.statuses.filter((status: FieldInterventionEntity.StatusEnum) => status !== value),
            };
        });
    }

    private loadForms(page: number): Promise<FieldInterventionFormControllerGet200Response> {
        return firstValueFrom(
            this.formService.fieldInterventionFormControllerGet({
                ...this.paginationParameters,
                fieldIntervention: this.fieldInterventionBloc.value()?.id,
                order: OrderEnum.ASC,
                orderBy: 'name',
                page: page,
                take: DEFAULT_TAKE,
            }),
        );
    }

    private loadPeople(page: number): Promise<PersonControllerGet200Response> {
        return firstValueFrom(
            this.peopleServiceSdk.personControllerGetFieldInterventionOperators({
                ...defaultPaginationParameters,
                fieldInterventionIds: [this.fieldInterventionBloc.value()!.id],
                order: 'ASC',
                orderBy: 'familyName',
                page: page,
                take: DEFAULT_TAKE,
            }),
        );
    }

    private loadStatuses(): Promise<FieldInterventionEntity.StatusEnum[]> {
        return firstValueFrom(
            this.fieldInterventionEntitiesServiceSdk.fieldInterventionEntityControllerGetStatuses({
                id: this.fieldInterventionBloc.value()!.id,
            }),
        );
    }
}
